<template>
  <div class="new_chart_container">
    <div ref="widgetContainer" class="traderboard-mount"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

declare global {
  interface Window {
    TraderverseCandleStickChartKline: any;
  }
}

const props = defineProps<{
  widget: any;
  isCreateView?: boolean;
  showGrid?: boolean;
}>();

const widgetContainer = ref<HTMLDivElement | null>(null);
let chartInstance: { destroy?: () => void } | null = null;
let initToken = 0;

const SCRIPT_SRC =
  'https://traderboard-dev.traderverse.io/sdk/traderverse-widget.min.js';

const PERIOD_MAP: Record<string, string> = {
  '1d': '1D',
  '5d': '5D',
  '1mo': '1M',
  '3mo': '3M',
  '6mo': '6M',
  '1y': '1Y',
  '2y': '1Y',
  '5y': '5Y',
  '10y': '5Y',
};

const INTERVAL_MAP: Record<string, string> = {
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '2h': '2h',
  '4h': '4h',
  '1d': '1D',
  '5d': '1D',
  '1wk': '1W',
  '1w': '1W',
  '1mo': '1M',
};

const mapPeriod = (duration?: string) => {
  if (!duration) return '1M';
  const key = String(duration).trim().toLowerCase();
  return PERIOD_MAP[key] || String(duration).toUpperCase().replace(/MO$/, 'M');
};

const mapInterval = (interval?: string) => {
  if (!interval) return '1D';
  const key = String(interval).trim().toLowerCase();
  return INTERVAL_MAP[key] || String(interval);
};

const getChartSymbol = () => {
  const stock = props.widget?.customData?.stock;
  if (!stock) return undefined;

  const candidates = [stock.stockId, stock._id, stock.symbol, stock.ticker]
    .map((v) => (v != null ? String(v).trim() : ''))
    .filter(Boolean);

  const withExchange = candidates.find((v) => v.includes('_'));
  if (withExchange) return withExchange;

  const symbol = candidates[0];
  if (!symbol) return undefined;

  const exchange = String(stock.exchange || 'NASDAQ')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
  return `${symbol}_${exchange || 'NASDAQ'}`;
};

const destroyChart = () => {
  if (chartInstance?.destroy) {
    try {
      chartInstance.destroy();
    } catch {
      /* ignore */
    }
  }
  chartInstance = null;
  if (widgetContainer.value) {
    widgetContainer.value.innerHTML = '';
  }
};

const initWidget = async () => {
  const token = ++initToken;
  if (!widgetContainer.value || !window.TraderverseCandleStickChartKline) return;

  const stock = props.widget?.customData?.stock;
  const chartSymbol = getChartSymbol();
  if (!chartSymbol) return;

  await nextTick();
  if (token !== initToken || !widgetContainer.value) return;

  destroyChart();

  // Fresh mount node avoids closed-shadowRoot re-attach failures on re-init
  const mountEl = document.createElement('div');
  mountEl.className = 'traderboard-chart-host';
  mountEl.style.width = '100%';
  mountEl.style.minHeight = '360px';
  mountEl.style.height = '400px';
  widgetContainer.value.appendChild(mountEl);

  try {
    chartInstance = new window.TraderverseCandleStickChartKline({
      container: mountEl,
      symbol: chartSymbol,
      stock,
      theme: 'dark',
      height: '400px',
      width: '100%',
      autosize: true,
      isSymbolSelectable: false,
      period: mapPeriod(props.widget?.customData?.duration),
      interval: mapInterval(props.widget?.customData?.interval),
    });
  } catch (e) {
    console.error('Failed to init traderboard chart', e);
  }
};

const loadScript = (): Promise<void> => {
  if (window.TraderverseCandleStickChartKline) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(
    `script[src="${SCRIPT_SRC}"]`
  ) as HTMLScriptElement | null;

  if (existingScript) {
    return new Promise((resolve, reject) => {
      if (window.TraderverseCandleStickChartKline) {
        resolve();
        return;
      }
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load traderboard chart SDK')),
        { once: true }
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load traderboard chart SDK'));
    document.body.appendChild(script);
  });
};

onMounted(async () => {
  try {
    await loadScript();
    await initWidget();
  } catch (e) {
    console.error(e);
  }
});

watch(
  () => [
    props.widget?.customData?.stock?.stockId,
    props.widget?.customData?.stock?.symbol,
    props.widget?.customData?.stock?.exchange,
    props.widget?.customData?.duration,
    props.widget?.customData?.interval,
  ],
  () => {
    if (window.TraderverseCandleStickChartKline) {
      initWidget();
    }
  }
);

onBeforeUnmount(() => {
  initToken += 1;
  destroyChart();
});
</script>

<style scoped>
.new_chart_container {
  width: 100%;
  min-height: 400px;
}

.traderboard-mount,
.traderboard-chart-host {
  width: 100%;
  min-height: 360px;
}
</style>
